import React, { ReactNode } from "react";
import {
  Box,
  Checkbox,
  Typography,
  FormControl,
  FormLabel,
  useTheme,
  styled,
} from "@mui/material";
import {
  Control,
  Controller,
  ControllerProps,
  useFormState,
} from "react-hook-form";

type FormCheckboxGroupProps = {
  name: string;
  control: Control<any, any>;
  label?: ReactNode;
  controlProps?: Partial<ControllerProps>;
  items: { value: string; label: string }[];
  multiple?: boolean;
  disabled?: boolean;
};

const CheckboxItemLabel = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
}));

const FormCheckboxGroup = ({
  name,
  control,
  label,
  controlProps,
  items,
  multiple = false,
  disabled = false,
}: FormCheckboxGroupProps) => {
  const { palette } = useTheme();
  const isRequired = !!controlProps?.rules?.required;

  // Check if form has been submitted using useFormState
  const formState = useFormState({ control });
  // Show errors if form is submitted OR if form mode allows immediate feedback
  const shouldShowError = formState.isSubmitted ?? true;

  return (
    <FormControl component="fieldset" sx={{ width: "100%" }}>
      <FormLabel component="legend" sx={{ width: "100%", display: "flex" }}>
        <Box
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: "#373533",
            paddingBottom: "2px",
            display: "flex",
          }}
        >
          {label}{" "}
          {isRequired && (
            <Typography sx={{ color: "red", paddingLeft: "4px" }}>*</Typography>
          )}
        </Box>
      </FormLabel>
      <Controller
        name={name}
        control={control}
        {...controlProps}
        render={({
          field: { onChange, value },
          fieldState: { error, isTouched },
        }) => {
          const handleChange = (itemValue: string) => {
            if (multiple) {
              const currentValues = Array.isArray(value) ? value : [];
              const newValues = currentValues.includes(itemValue)
                ? currentValues.filter((v) => v !== itemValue)
                : [...currentValues, itemValue];
              onChange(newValues);
            } else {
              onChange(itemValue);
            }
          };

          const isChecked = (itemValue: string) => {
            if (multiple) {
              return Array.isArray(value) && value.includes(itemValue);
            }
            return value === itemValue;
          };

          // Show error if form is submitted OR if field is touched (for immediate feedback in onChange/onBlur modes)
          const showError = (shouldShowError || isTouched) && !!error?.message;

          return (
            <Box>
              <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
                {items.map((item) => (
                  <CheckboxItemLabel
                    key={item.value}
                    onClick={() => !disabled && handleChange(item.value)}
                    sx={{ cursor: disabled ? "default" : "pointer" }}
                  >
                    <Checkbox
                      sx={{
                        color: "#0076CE",
                        "&.Mui-checked": {
                          color: "#0076CE",
                        },
                      }}
                      checked={isChecked(item.value)}
                      onChange={() => !disabled && handleChange(item.value)}
                      disabled={disabled}
                    />
                    <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
                      {item.label}
                    </Typography>
                  </CheckboxItemLabel>
                ))}
              </Box>
              {showError && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: palette.errorText,
                    paddingTop: "4px",
                  }}
                >
                  {error?.message}
                </Typography>
              )}
            </Box>
          );
        }}
      />
    </FormControl>
  );
};

export default FormCheckboxGroup;
